import * as Sentry from '@sentry/react';
import jwtDecode from 'jwt-decode';

const EXPIRY_BUCKETS = [
    { limit: -86400, label: 'expired_gt_24h' },
    { limit: -3600, label: 'expired_1h_24h' },
    { limit: -60, label: 'expired_1m_1h' },
    { limit: 0, label: 'expired_lt_1m' },
    { limit: 60, label: 'valid_lt_1m' },
    { limit: 3600, label: 'valid_1m_1h' },
    { limit: 86400, label: 'valid_1h_24h' },
    { limit: 604800, label: 'valid_1d_7d' },
    { limit: Infinity, label: 'valid_gt_7d' },
];

function expiryBucket(secondsUntilExpiry) {
    const bucket = EXPIRY_BUCKETS.find(({ limit }) => secondsUntilExpiry <= limit);
    return bucket?.label || 'unknown';
}

function getTokenSnapshot(token) {
    if (!token) {
        return {
            present: false,
            valid_format: null,
            expired: null,
            expiry_bucket: 'missing',
        };
    }

    try {
        const decoded = jwtDecode(token);
        const exp = Number(decoded?.exp);
        const now = Math.floor(Date.now() / 1000);
        const secondsUntilExpiry = Number.isFinite(exp) ? exp - now : null;

        return {
            present: true,
            valid_format: true,
            expired: secondsUntilExpiry !== null ? secondsUntilExpiry <= 0 : null,
            expiry_bucket: secondsUntilExpiry !== null ? expiryBucket(secondsUntilExpiry) : 'missing_exp',
            seconds_until_expiry: secondsUntilExpiry,
            user_id: decoded?.user_id || decoded?.userId || decoded?.sub || null,
            token_type: decoded?.token_type || null,
        };
    } catch {
        return {
            present: true,
            valid_format: false,
            expired: null,
            expiry_bucket: 'malformed',
        };
    }
}

function cleanExtra(extra = {}) {
    return Object.fromEntries(
        Object.entries(extra).filter(([, value]) => value !== undefined && value !== null && value !== '')
    );
}

/**
 * Breadcrumb for the access-token lifecycle around an API call, so a Sentry
 * event carries the sequence that led to it: whether the token was refreshed
 * BEFORE the request (near expiry), whether that pre-refresh failed and the
 * request went out anyway, whether a 401 triggered the fallback refresh and
 * retry, and whether the retry itself still failed. Breadcrumbs only: these
 * are expected paths, not errors.
 */
export function captureAuthRefreshStage(stage, extra = {}) {
    try {
        Sentry.addBreadcrumb({
            category: 'auth.refresh',
            level: 'info',
            message: stage,
            data: cleanExtra(extra),
        });
    } catch {
        // Telemetry must never affect the request.
    }
}

export function captureAuthSessionCleared(reason = 'unknown', extra = {}) {
    try {
        const access = getTokenSnapshot(localStorage.getItem('accessToken'));
        const refresh = getTokenSnapshot(localStorage.getItem('refreshToken'));
        const route = window.location?.pathname || 'unknown';

        Sentry.captureMessage('CRM auth session cleared', {
            level: reason === 'manual_logout' ? 'info' : 'warning',
            fingerprint: ['crm-auth-session-cleared', reason],
            tags: {
                feature: 'auth_session',
                auth_reason: reason,
                route,
                access_present: String(access.present),
                access_expiry_bucket: access.expiry_bucket,
                refresh_present: String(refresh.present),
                refresh_expiry_bucket: refresh.expiry_bucket,
                ...(extra.status !== undefined && extra.status !== null && { refresh_status: String(extra.status) }),
            },
            contexts: {
                auth_session: {
                    reason,
                    route,
                    access,
                    refresh,
                    ...cleanExtra(extra),
                },
            },
        });
    } catch {
        // Telemetry must never block logout/session clearing.
    }
}

export const __authSessionTelemetryTestUtils = {
    getTokenSnapshot,
    expiryBucket,
};
